/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Quiz navigation following the established Profile/Community/Upload design.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { auth } from "../firebase";
import CommunityNavBadge from "./CommunityNavBadge";
import SearchModal from "./SearchModal";

function NavIcon({ name }) {
  const attributes = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  if (name === "home") {
    return <svg {...attributes}><path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" /></svg>;
  }
  if (name === "explore") {
    return <svg {...attributes}><circle cx="12" cy="12" r="9" /><path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" /></svg>;
  }
  if (name === "camera") {
    return (
      <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
    );
  }
  if (name === "profile") {
    return <svg {...attributes}><circle cx="12" cy="7.5" r="4" /><path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" /></svg>;
  }
  return <svg {...attributes}><circle cx="9" cy="8" r="3" /><path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" /></svg>;
}

export default function BioIndexQuizNavigation({ sourceUID }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreMenuRef = useRef(null);
  const exploreMenuId = "bioindex-quiz-explore-menu";
  const ownProfile = !sourceUID || sourceUID === auth.currentUser?.uid;
  const profilePath = ownProfile ? "/profile" : `/profile/${sourceUID}`;
  const explorePath = ownProfile ? "/explore" : `/explore/${sourceUID}`;

  useEffect(() => {
    if (!exploreOpen) return undefined;
    function closeOutside(event) {
      if (!exploreMenuRef.current?.contains(event.target)) setExploreOpen(false);
    }
    function handleKey(event) {
      if (event.key === "Escape") setExploreOpen(false);
    }
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [exploreOpen]);

  const openSearch = () => {
    setExploreOpen(false);
    setSearchOpen(true);
  };
  const openVoiceSearch = () => {
    setExploreOpen(false);
    setVoiceOpen(true);
  };

  return (
    <>
      {/* The desktop header shares Profile/Upload's 1360px navigation width. */}
      <div className="bioindex-quiz-desktop-nav">
        <div className="bioindex-quiz-navbar-inner">
          <Link to="/" aria-label="BioIndex Home" className="bioindex-quiz-brand">
            <img src="/favicon.ico" alt="" />
            <span className="bioindex-quiz-brand-wordmark">
              <strong>BioIndex</strong>
              <small>Taxonomy Challenge</small>
            </span>
          </Link>
          <span className="bioindex-quiz-brand-divider" aria-hidden="true" />
          <nav aria-label="BioIndex navigation" className="bioindex-quiz-navbar-links">
            <Link to="/">Home</Link>
            <Link to={explorePath}>Explore Taxonomy</Link>
            <Link to="/upload">Add Photos</Link>
            <Link to={profilePath}>Profile</Link>
            <Link to="/forum" className="bioindex-quiz-community-link">
              Community
              <CommunityNavBadge />
            </Link>
            <button type="button" onClick={openSearch}>Search</button>
          </nav>
        </div>
      </div>

      {/* Five identical positions to the approved Community/Upload mobile navigation. */}
      <nav className="bioindex-m-bottom-bar bioindex-quiz-mobile-nav" aria-label="Primary navigation">
        <div className="bioindex-m-bottom-grid">
          <Link className="bioindex-m-bottom-item" to="/" aria-label="Home">
            <NavIcon name="home" />
            <span>Home</span>
          </Link>
          <div className="bioindex-m-explore-wrap" ref={exploreMenuRef}>
            <button
              type="button"
              className={`bioindex-m-bottom-item bioindex-m-explore-trigger${exploreOpen ? " bioindex-m-explore-active" : ""}`}
              onClick={() => setExploreOpen((open) => !open)}
              aria-expanded={exploreOpen}
              aria-controls={exploreMenuId}
              aria-label="Explore or Search"
            >
              <NavIcon name="explore" />
              <span>Explore</span>
            </button>
            {exploreOpen && (
              <div id={exploreMenuId} className="bioindex-m-explore-menu" role="group" aria-label="Explore options">
                <Link to={explorePath} className="bioindex-m-explore-choice" onClick={() => setExploreOpen(false)}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><NavIcon name="explore" /></span>
                  <span>Explore taxonomy</span>
                </Link>
                <button type="button" className="bioindex-m-explore-choice" onClick={openSearch}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><img src="/Search.png" alt="" /></span>
                  <span>Search BioIndex</span>
                </button>
                <button type="button" className="bioindex-m-explore-choice" onClick={openVoiceSearch}>
                  <span className="bioindex-m-explore-choice-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" /></svg></span>
                  <span>Voice Search</span>
                </button>
              </div>
            )}
          </div>
          <Link className="bioindex-m-bottom-item bioindex-m-add" to="/upload" aria-label="Add Photos">
            <span className="bioindex-m-add-circle" aria-hidden="true"><NavIcon name="camera" /></span>
            <span>Add Photos</span>
          </Link>
          <Link className="bioindex-m-bottom-item" to={profilePath} aria-label="Profile">
            <NavIcon name="profile" />
            <span>Profile</span>
          </Link>
          <Link className="bioindex-m-bottom-item" to="/forum" aria-label="Community">
            <span className="bioindex-m-current-icon">
              <NavIcon name="community" />
              <CommunityNavBadge />
            </span>
            <span>Community</span>
          </Link>
        </div>
      </nav>

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        uid={auth.currentUser?.uid}
      />
      {voiceOpen && <SearchModal open voiceOnly onClose={() => setVoiceOpen(false)} uid={auth.currentUser?.uid} />}
    </>
  );
}
